import { useCallback, useEffect, useState } from 'react';
import { Landmark, ExternalLink, ShieldCheck, AlertCircle } from 'lucide-react';
import { supabase } from '../../supabaseClient';

const money = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;
const when = (iso) => (iso ? new Date(iso).toLocaleDateString() : '—');

// Consignor payouts. Bank details are never typed here — "Set up payouts" hands
// off to Stripe's own hosted onboarding, which collects the account and routing
// numbers and runs identity verification. All we ever store is an account id.
export default function BankingTab() {
  const [status, setStatus] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data, error: err } = await supabase.functions.invoke('shinyvault-payouts', {
      body: { action: 'account_status' },
    });
    if (err || data?.error) { setError(data?.error || err.message); return; }
    setError('');
    setStatus(data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const connect = async () => {
    setBusy(true);
    const { data, error: err } = await supabase.functions.invoke('shinyvault-payouts', {
      body: { action: 'create_account_link' },
    });
    setBusy(false);
    if (err || data?.error) { setError(data?.error || err.message); return; }
    window.location.href = data.url;
  };

  if (error) {
    return (
      <div className="sv-card" style={{ padding: 20, display: 'flex', gap: 10 }}>
        <AlertCircle size={18} style={{ flexShrink: 0, marginTop: 2 }} />
        <div>{error}</div>
      </div>
    );
  }
  if (!status) return <div style={{ padding: 20, color: 'var(--text-muted)' }}>Loading…</div>;

  const ready = status.connected && status.payouts_enabled;

  return (
    <div style={{ display: 'grid', gap: 18 }}>
      <div className="sv-card" style={{ padding: 22 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 6 }}>
          <Landmark size={18} />
          <h2 style={{ fontSize: '1.05rem', fontWeight: 800, margin: 0 }}>Payout account</h2>
        </div>

        {ready ? (
          <p style={{ margin: '6px 0 0', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 7 }}>
            <ShieldCheck size={16} /> Connected and ready. Payouts go to your bank on file with Stripe.
          </p>
        ) : (
          <>
            <p style={{ margin: '6px 0 16px', color: 'var(--text-muted)', lineHeight: 1.6 }}>
              {status.connected
                ? 'Stripe still needs a few details before it can pay you.'
                : 'Link your bank so your share can be paid out. Stripe handles this part, so your account numbers are never entered here or stored by us.'}
            </p>
            {status.needs?.length > 0 && (
              <ul style={{ margin: '0 0 16px', paddingLeft: 18, color: 'var(--text-muted)', fontSize: '.9rem' }}>
                {status.needs.map((n) => <li key={n}>{n.replace(/[._]/g, ' ')}</li>)}
              </ul>
            )}
            <button className="sv-btn" onClick={connect} disabled={busy}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              {busy ? 'Opening Stripe…' : status.connected ? 'Finish setup with Stripe' : 'Set up payouts with Stripe'}
              <ExternalLink size={15} />
            </button>
          </>
        )}
      </div>

      <div className="sv-card" style={{ padding: 22 }}>
        <div style={{ fontSize: '.78rem', letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--text-muted)' }}>
          Waiting to be paid
        </div>
        <div style={{ fontSize: '2rem', fontWeight: 900, marginTop: 4 }}>{money(status.balance_cents)}</div>
        <p style={{ margin: '8px 0 0', color: 'var(--text-muted)', fontSize: '.9rem', lineHeight: 1.6 }}>
          Your 75 percent of the profit on your own items. Profit is what a card sold for minus what it
          was bought for. Shipping is not part of it.
        </p>
      </div>

      <div className="sv-card" style={{ padding: 22 }}>
        <h2 style={{ fontSize: '1.05rem', fontWeight: 800, margin: '0 0 14px' }}>History</h2>
        {status.history?.length ? (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.9rem' }}>
              <thead>
                <tr style={{ textAlign: 'left', color: 'var(--text-muted)' }}>
                  <th style={{ padding: '6px 10px 6px 0' }}>Sold</th>
                  <th style={{ padding: '6px 10px' }}>Sold for</th>
                  <th style={{ padding: '6px 10px' }}>Cost</th>
                  <th style={{ padding: '6px 10px' }}>Profit</th>
                  <th style={{ padding: '6px 10px' }}>Your share</th>
                  <th style={{ padding: '6px 0 6px 10px' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {status.history.map((r) => (
                  <tr key={r.id} style={{ borderTop: '1px solid var(--border)' }}>
                    <td style={{ padding: '9px 10px 9px 0' }}>{when(r.created_at)}</td>
                    <td style={{ padding: '9px 10px' }}>{money(r.gross_cents)}</td>
                    <td style={{ padding: '9px 10px' }}>{money(r.cost_cents)}</td>
                    <td style={{ padding: '9px 10px' }}>{money(r.net_cents)}</td>
                    <td style={{ padding: '9px 10px', fontWeight: 700 }}>{money(r.owner_cents)}</td>
                    <td style={{ padding: '9px 0 9px 10px', color: 'var(--text-muted)' }}>
                      {r.status === 'paid' ? `Paid ${when(r.paid_at)}` : r.status}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p style={{ margin: 0, color: 'var(--text-muted)' }}>Nothing sold yet.</p>
        )}
      </div>
    </div>
  );
}
